import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronLeft, ChevronRight, Trash2, Archive, ArchiveRestore, Pencil, FileSpreadsheet, FileText, BarChart3, Users, ExternalLink, Eye, EyeOff, Globe, Loader2, Check } from "lucide-react";
import { getRaidCalendar, deleteRaid, deleteRaidsByDate, updateRaid, archiveRaid, unarchiveRaid, fetchWclParses, getWclStatus } from "@/lib/api";
import { CLASS_COLORS } from "@/lib/constants";
import { useTeams } from "@/lib/useTeams";
import { toast } from "sonner";

const TEAM_COLORS = { Monday: "#3b82f6", Thursday: "#f59e0b" };
const SOURCE_ICONS = { rpb: FileSpreadsheet, combat_log: FileText, data_analysis: BarChart3 };
const SOURCE_LABELS = { rpb: "RPB", combat_log: "Combat Log", data_analysis: "Data Analysis" };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  // Adjust to Monday-start week
  let startOffset = firstDay.getDay() - 1;
  if (startOffset < 0) startOffset = 6;

  const days = [];
  // Fill leading days from previous month
  for (let i = startOffset - 1; i >= 0; i--) {
    const d = new Date(year, month, -i);
    days.push({ date: d, inMonth: false });
  }
  // Fill current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({ date: new Date(year, month, i), inMonth: true });
  }
  // Fill trailing days
  while (days.length % 7 !== 0) {
    const d = new Date(year, month + 1, days.length - lastDay.getDate() - startOffset + 1);
    days.push({ date: d, inMonth: false });
  }
  return days;
}

function formatDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function SourceBadge({ source }) {
  const Icon = SOURCE_ICONS[source] || FileSpreadsheet;
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
      <Icon className="h-2.5 w-2.5" /> {SOURCE_LABELS[source] || source}
    </span>
  );
}

export default function RaidCalendar({ onNavigate }) {
  const teams = useTeams();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [raids, setRaids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRaid, setSelectedRaid] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [filterTeam, setFilterTeam] = useState("all");
  const [dayDeleteTarget, setDayDeleteTarget] = useState(null); // date string
  const [wclCodes, setWclCodes] = useState(["", ""]);
  const [wclLoading, setWclLoading] = useState(false);
  const [wclStatus, setWclStatus] = useState(null); // {linked, report_code, players_with_parses}

  const loadRaids = async () => {
    setLoading(true);
    try {
      const team = filterTeam === "all" ? null : filterTeam;
      const data = await getRaidCalendar(team, showArchived);
      setRaids(data);
    } catch (e) {
      console.error("Failed to load calendar", e);
    }
    setLoading(false);
  };

  useEffect(() => { loadRaids(); }, [filterTeam, showArchived]);

  const raidsByDate = useMemo(() => {
    const map = {};
    for (const r of raids) {
      const key = r.date;
      if (!map[key]) map[key] = [];
      map[key].push(r);
    }
    return map;
  }, [raids]);

  const days = useMemo(() => getMonthDays(year, month), [year, month]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };

  const handleDelete = async (raid) => {
    if (!window.confirm(`Delete "${raid.raid_name}"? This removes all player data for this raid.`)) return;
    try {
      await deleteRaid(raid.id);
      toast.success("Raid deleted");
      setSelectedRaid(null);
      loadRaids();
    } catch (e) { toast.error("Failed to delete raid"); }
  };

  const handleArchive = async (raid) => {
    try {
      if (raid.archived) {
        await unarchiveRaid(raid.id);
        toast.success("Raid restored");
      } else {
        await archiveRaid(raid.id);
        toast.success("Raid archived");
      }
      setSelectedRaid(null);
      loadRaids();
    } catch (e) { toast.error("Failed to update raid"); }
  };

  const handleRename = async () => {
    if (!newName.trim() || !selectedRaid) return;
    try {
      await updateRaid(selectedRaid.id, { raid_name: newName.trim() });
      toast.success("Raid renamed");
      setRenaming(false);
      setSelectedRaid(null);
      loadRaids();
    } catch (e) { toast.error("Failed to rename raid"); }
  };

  const handleRaidClick = (raid) => {
    setSelectedRaid(raid);
    setNewName(raid.raid_name);
    setRenaming(false);
    setWclCodes(["", ""]);
    setWclStatus(null);
    // Check WCL status
    getWclStatus(raid.id).then((s) => {
      setWclStatus(s);
      if (s.report_code) {
        const codes = s.report_code.split(", ");
        setWclCodes(codes.length >= 2 ? codes : [...codes, ""]);
      }
    }).catch(() => {});
  };

  const handleDeleteDay = async () => {
    if (!dayDeleteTarget) return;
    try {
      const res = await deleteRaidsByDate(dayDeleteTarget);
      toast.success(`Deleted ${res.count} raid(s) for ${dayDeleteTarget}`);
      setDayDeleteTarget(null);
      setSelectedRaid(null);
      loadRaids();
    } catch (e) {
      toast.error("Failed to delete raids for this day");
    }
  };

  const handleFetchParses = async () => {
    const codes = wclCodes.map(c => c.trim()).filter(Boolean);
    if (!selectedRaid || codes.length === 0) {
      toast.error("Enter at least one WarcraftLogs report code");
      return;
    }

    setWclLoading(true);
    try {
      const result = await fetchWclParses(selectedRaid.id, codes);
      if (result.status === "no_data") {
        toast.warning(result.message);
      } else {
        toast.success(`Updated ${result.updated}/${result.total_in_raid} players from ${codes.length} report(s)`);
        if (result.unmatched_wcl_players?.length > 0) {
          toast.info(`Unmatched: ${result.unmatched_wcl_players.join(", ")}`);
        }
        setWclStatus({ linked: true, report_code: codes.join(", "), players_with_parses: result.updated });
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to fetch parses";
      toast.error(msg);
    }
    setWclLoading(false);
  };

  const goToRaid = (raid) => {
    onNavigate(`/raid/${raid.id}`);
  };

  const today = formatDateKey(new Date());

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="raid-calendar-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="heading text-4xl md:text-5xl font-bold tracking-tight uppercase">Raid Calendar</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage and navigate raid imports</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filterTeam} onValueChange={setFilterTeam}>
            <SelectTrigger data-testid="calendar-team-filter" className="w-36 h-8 text-xs bg-card/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="toggle-archived-btn" variant="ghost" size="sm"
            onClick={() => setShowArchived(!showArchived)}
            className={`text-xs gap-1.5 ${showArchived ? "text-foreground" : "text-muted-foreground"}`}>
            {showArchived ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            {showArchived ? "Showing Archived" : "Archived Hidden"}
          </Button>
        </div>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <Button data-testid="prev-month-btn" variant="ghost" size="icon" onClick={prevMonth} className="h-8 w-8">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h2 className="heading text-xl font-bold uppercase tracking-wider">
          {MONTHS[month]} {year}
        </h2>
        <Button data-testid="next-month-btn" variant="ghost" size="icon" onClick={nextMonth} className="h-8 w-8">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="border border-border/30 rounded-sm overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-7 bg-card/30">
          {DAYS.map((d) => (
            <div key={d} className="text-center text-xs font-mono uppercase tracking-wider text-muted-foreground py-2 border-b border-border/20">
              {d}
            </div>
          ))}
        </div>

        {/* Days */}
        <div className="grid grid-cols-7">
          {days.map((day, i) => {
            const key = formatDateKey(day.date);
            const dayRaids = raidsByDate[key] || [];
            const isToday = key === today;
            const isRaidDay = day.date.getDay() === 1 || day.date.getDay() === 4; // Mon or Thu
            return (
              <div key={i} data-testid={`calendar-day-${key}`}
                className={`group min-h-[100px] border-b border-r border-border/10 p-1.5 transition-colors
                  ${day.inMonth ? "" : "opacity-30"}
                  ${isToday ? "bg-accent/10" : ""}
                  ${isRaidDay && day.inMonth ? "bg-card/20" : ""}
                `}>
                <div className={`text-xs font-mono mb-1 flex items-center justify-between ${isToday ? "text-foreground font-bold" : "text-muted-foreground"}`}>
                  <span>{day.date.getDate()}</span>
                  {dayRaids.length > 0 && day.inMonth && (
                    <button data-testid={`delete-day-${key}`}
                      onClick={(e) => { e.stopPropagation(); setDayDeleteTarget(key); }}
                      className="opacity-0 group-hover:opacity-100 hover:text-red-400 text-muted-foreground transition-opacity p-0.5"
                      title="Delete all raids this day">
                      <Trash2 className="h-2.5 w-2.5" />
                    </button>
                  )}
                </div>
                <div className="space-y-0.5">
                  {dayRaids.map((r) => (
                    <button key={r.id} data-testid={`calendar-raid-${r.id}`}
                      onClick={() => handleRaidClick(r)}
                      className={`w-full text-left px-1.5 py-0.5 rounded-sm text-[11px] font-medium truncate transition-colors hover:brightness-125 cursor-pointer
                        ${r.archived ? "opacity-40 line-through" : ""}
                      `}
                      style={{
                        backgroundColor: `${TEAM_COLORS[r.team] || "#666"}15`,
                        borderLeft: `2px solid ${TEAM_COLORS[r.team] || "#666"}`,
                        color: TEAM_COLORS[r.team] || "#ccc",
                      }}>
                      {r.raid_name?.substring(0, 25) || "Unnamed"}
                      {r.has_wcl && <span className="ml-1 text-purple-400" title="WCL Linked">W</span>}
                      {r.has_data_analysis && <span className="ml-1 text-green-500">+</span>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-1.5 rounded-sm" style={{ backgroundColor: TEAM_COLORS.Monday }} /> Monday
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-1.5 rounded-sm" style={{ backgroundColor: TEAM_COLORS.Thursday }} /> Thursday
        </span>
        <span className="flex items-center gap-1.5">
          <span className="text-purple-400 font-bold">W</span> WCL Linked
        </span>
        <span className="flex items-center gap-1.5">
          <span className="text-green-500 font-bold">+</span> Has Data Analysis
        </span>
      </div>

      {/* Raid Detail Dialog */}
      <Dialog open={!!selectedRaid} onOpenChange={(o) => { if (!o) setSelectedRaid(null); }}>
        <DialogContent className="max-w-md bg-background">
          {selectedRaid && (
            <>
              <DialogHeader>
                <DialogTitle className="heading text-lg uppercase tracking-tight pr-8">
                  {renaming ? (
                    <div className="flex gap-2">
                      <Input data-testid="rename-raid-input" value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleRename()}
                        className="bg-card/50 text-sm" autoFocus />
                      <Button size="sm" onClick={handleRename} className="text-xs">Save</Button>
                    </div>
                  ) : (
                    <span>{selectedRaid.raid_name}</span>
                  )}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {/* Info */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="stat-card">
                    <span className="text-[10px] font-mono uppercase text-muted-foreground">Date</span>
                    <p className="text-sm font-mono font-bold">{selectedRaid.date}</p>
                  </div>
                  <div className="stat-card">
                    <span className="text-[10px] font-mono uppercase text-muted-foreground">Team</span>
                    <p className="text-sm font-bold" style={{ color: TEAM_COLORS[selectedRaid.team] }}>{selectedRaid.team}</p>
                  </div>
                  <div className="stat-card">
                    <span className="text-[10px] font-mono uppercase text-muted-foreground">Players</span>
                    <p className="text-sm font-mono font-bold flex items-center gap-1">
                      <Users className="h-3.5 w-3.5 text-muted-foreground" /> {selectedRaid.player_count}
                    </p>
                  </div>
                  <div className="stat-card">
                    <span className="text-[10px] font-mono uppercase text-muted-foreground">Data Sources</span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {selectedRaid.sources?.map((s) => <SourceBadge key={s} source={s} />)}
                    </div>
                  </div>
                </div>

                {selectedRaid.boss_name && (
                  <div className="text-xs text-muted-foreground">
                    Boss: <span className="text-foreground">{selectedRaid.boss_name}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="view-raid-btn" size="sm" onClick={() => goToRaid(selectedRaid)}
                    className="gap-1.5 text-xs rounded-sm bg-foreground text-background hover:bg-foreground/90">
                    <ExternalLink className="h-3 w-3" /> View Raid Data
                  </Button>
                  <Button data-testid="rename-raid-btn" variant="outline" size="sm" onClick={() => setRenaming(!renaming)}
                    className="gap-1.5 text-xs rounded-sm">
                    <Pencil className="h-3 w-3" /> Rename
                  </Button>
                  <Button data-testid="archive-raid-btn" variant="outline" size="sm" onClick={() => handleArchive(selectedRaid)}
                    className="gap-1.5 text-xs rounded-sm">
                    {selectedRaid.archived
                      ? <><ArchiveRestore className="h-3 w-3" /> Restore</>
                      : <><Archive className="h-3 w-3" /> Archive</>}
                  </Button>
                  <Button data-testid="delete-raid-btn" variant="destructive" size="sm" onClick={() => handleDelete(selectedRaid)}
                    className="gap-1.5 text-xs rounded-sm">
                    <Trash2 className="h-3 w-3" /> Delete
                  </Button>
                </div>

                {/* WarcraftLogs Parse Fetch */}
                <div className="pt-2 border-t border-border/20" data-testid="wcl-calendar-section">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="h-3.5 w-3.5 text-purple-400" />
                    <span className="text-xs font-mono uppercase tracking-wider text-purple-400">WarcraftLogs</span>
                    {wclStatus?.linked && (
                      <Badge variant="outline" className="text-[9px] text-purple-400 border-purple-400/30 gap-1">
                        <Check className="h-2.5 w-2.5" /> {wclStatus.players_with_parses} parses
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {wclCodes.map((code, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <span className="text-[10px] font-mono text-muted-foreground w-3">{i + 1}</span>
                        <Input
                          data-testid={`wcl-calendar-input-${i}`}
                          value={code}
                          onChange={(e) => {
                            const next = [...wclCodes];
                            next[i] = e.target.value;
                            setWclCodes(next);
                          }}
                          placeholder={`Report #${i + 1}...`}
                          className="h-7 text-xs bg-card/50"
                        />
                      </div>
                    ))}
                    <div className="flex items-center justify-between">
                      <button onClick={() => setWclCodes([...wclCodes, ""])}
                        className="text-[10px] text-purple-400 hover:text-purple-300">
                        + Add report
                      </button>
                      <Button data-testid="wcl-calendar-fetch-btn" size="sm" onClick={handleFetchParses}
                        disabled={wclLoading}
                        className="gap-1.5 text-xs rounded-sm bg-purple-600 hover:bg-purple-700 text-white flex-shrink-0">
                        {wclLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Globe className="h-3 w-3" />}
                        {wclLoading ? "..." : "Fetch"}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Day Confirmation Dialog */}
      <Dialog open={!!dayDeleteTarget} onOpenChange={(o) => { if (!o) setDayDeleteTarget(null); }}>
        <DialogContent className="max-w-sm bg-background">
          <DialogHeader>
            <DialogTitle className="heading text-lg uppercase tracking-tight">Delete All Raids</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will permanently delete <span className="text-red-400 font-bold">all raids and their player data</span> for <span className="font-mono text-foreground">{dayDeleteTarget}</span>.
          </p>
          <div className="flex gap-2 justify-end mt-2">
            <Button data-testid="cancel-delete-day" variant="outline" size="sm" onClick={() => setDayDeleteTarget(null)} className="text-xs">
              Cancel
            </Button>
            <Button data-testid="confirm-delete-day" variant="destructive" size="sm" onClick={handleDeleteDay} className="gap-1.5 text-xs">
              <Trash2 className="h-3 w-3" /> Delete Day
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
